import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Fab,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import SuccessMessage from '../../../components/successMessage';
import ErrorMessage from '../../../components/errorMessage';
import Loading from '../../../components/loading';
import CityFormModal from '../cityFormModal';
import { useCityCatalog } from '../../../hooks/useCityCatalog';
import { useDeleteCity } from '../hooks/useDeleteCity';

function CityListMobile() {
  const navigate = useNavigate();

  const { cities, isLoading, error: listError, refetch: getCities } = useCityCatalog();
  const { remove } = useDeleteCity();

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (listError) {
      setShowErrorMessage(true);
      setErrorMessage(listError);
    }
  }, [listError]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCityId, setSelectedCityId] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const clickMenu = (event, cityId) => {
    setAnchorEl(event.currentTarget);
    setSelectedCityId(cityId);
  };

  const closeMenu = () => setAnchorEl(null);

  const openCreateModal = () => {
    setSelectedCity(null);
    setModalOpen(true);
  };

  const openEditModal = () => {
    const city = cities.find((item) => item.id === selectedCityId);
    if (city) {
      setSelectedCity(city);
      setModalOpen(true);
    }
    closeMenu();
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedCity(null);
  };

  const openConfirmModal = () => {
    setAnchorEl(null);
    setConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
    setSelectedCityId(null);
  };

  const handleCitySaved = (savedCity, message) => {
    getCities();
    setSuccessMessage(message ?? 'Cidade salva com sucesso.');
    setShowSuccessMessage(true);
  };

  const confirmDelete = () => {
    setConfirmModalOpen(false);
    remove(selectedCityId)
      .then((returnFromApi) => {
        getCities();
        setSuccessMessage(returnFromApi.data ?? 'Cidade excluída com sucesso.');
        setShowSuccessMessage(true);
      })
      .catch((returnFromApi) => {
        setShowErrorMessage(true);
        setErrorMessage(returnFromApi.response?.data ?? 'Erro ao excluir cidade.');
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.response?.status === 401) navigate('/expired-session');
      })
      .finally(() => {
        setSelectedCityId(null);
      });
  };

  return (
    <>
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Box sx={{ p: 2 }}>
        <Card sx={{ borderRadius: '16px', boxShadow: 'none' }}>
          <CardHeader
            title="Cidades"
            titleTypographyProps={{ variant: 'h6', fontWeight: 'bold', color: 'text.secondary' }}
          />
          <CardContent>
            <List>
              {cities.length === 0 && !isLoading ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2, textAlign: 'center' }}
                >
                  Nenhuma cidade cadastrada
                </Typography>
              ) : (
                cities.map((city) => (
                  <React.Fragment key={city.id}>
                    <ListItem
                      secondaryAction={
                        <IconButton onClick={(event) => clickMenu(event, city.id)}>
                          <MoreVertIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={city.name}
                        secondary={`${city.state} - ${city.stateDescription}`}
                        primaryTypographyProps={{ fontWeight: '600' }}
                      />
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))
              )}
            </List>
          </CardContent>
        </Card>
      </Box>

      <Fab
        color="primary"
        aria-label="adicionar cidade"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={openCreateModal}
      >
        <AddIcon />
      </Fab>

      <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={closeMenu}>
        <MenuItem onClick={openEditModal}>Editar</MenuItem>
        <MenuItem onClick={openConfirmModal} sx={{ color: 'error.main' }}>
          Excluir
        </MenuItem>
      </Menu>

      <CityFormModal
        open={isModalOpen}
        onClose={closeModal}
        onSave={handleCitySaved}
        city={selectedCity}
      />

      <Dialog open={isConfirmModalOpen} onClose={closeConfirmModal}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Você tem certeza que deseja excluir esta cidade? Ela deixará de aparecer nas listas de
            seleção do sistema.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmModal}>Cancelar</Button>
          <Button onClick={confirmDelete} variant="contained" color="error" autoFocus>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CityListMobile;
